import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { CheckCircle2, LoaderCircle } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";

const buttonVariants = cva(
  "pointer-events-auto inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-[transform,box-shadow,filter,background-color] duration-150 ease-out hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-full px-3",
        lg: "h-11 rounded-full px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type TButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    success?: boolean;
    error?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, TButtonProps>(
  ({ className, variant, size, asChild = false, loading, success, error, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = (disabled ?? false) || (loading ?? false);

    const content =
      !asChild && loading ? (
        <LoaderCircle
          className="h-4 w-4 animate-spin"
          aria-hidden
        />
      ) : !asChild && success ? (
        <>
          <CheckCircle2
            className="h-4 w-4 shrink-0"
            aria-hidden
          />
          <span>{children}</span>
        </>
      ) : (
        children
      );

    const stateClasses = cn(success && "bg-green-600 text-white hover:bg-green-600", error && "button-error-shake");

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), stateClasses, !asChild && success && "gap-2")}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {content}
      </Comp>
    );
  }
);

Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
